/**
 * API 문서 작성 헬퍼 함수
 * 반복적인 JSX 패턴을 간소화하여 문서 작성을 용이하게 함
 */

/**
 * API 파라미터 설명 박스 생성
 * @param {Object} params - 파라미터 객체
 * @param {string} params.title - 파라미터 제목
 * @param {string|Array<string>} params.sub - 서브 설명 (문자열 또는 배열)
 * @param {any} params.defaultValue - 기본값
 * @param {Array} params.allowedValues - 허용값 배열 (문자열 배열 또는 [값, 설명] 배열)
 * @param {Array<string>} params.notes - 주의사항 배열
 * @returns {JSX.Element} 설명 박스 JSX
 */
export const apiParamDesc = ({ title, sub, defaultValue, allowedValues, notes }) => {
  // 단순 텍스트만 있는 경우
  if (!sub && defaultValue === undefined && !allowedValues && !notes) {
    return title;
  }

  // allowedValues 배열의 타입 감지 (문자열 배열 vs [값, 설명] 배열)
  const hasDescriptions = allowedValues && Array.isArray(allowedValues[0]);

  // sub가 배열인지 문자열인지 확인
  const isSubArray = Array.isArray(sub);

  return (
    <div>
      {title}
      {sub && (
        <div className='description'>
          {isSubArray ? (
            <>
              {sub.map((item, index) => (
                <div key={index} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </>
          ) : (
            <span dangerouslySetInnerHTML={{ __html: sub }} />
          )}
        </div>
      )}

      {defaultValue !== undefined && (
        <>
          <div className='description' style={{ padding: '12px 0' }}>
            Default value
          </div>
          <table>
            <thead>
              <tr>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>
                    <code>{defaultValue}</code>
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {allowedValues && (
        <>
          <div className='description' style={{ padding: '12px 0' }}>
            Allowed values
          </div>
          <table>
            <thead>
              <tr>
                <th>Value</th>
                {hasDescriptions && <th>Description</th>}
              </tr>
            </thead>
            <tbody>
              {allowedValues.map((item, index) => {
                const value = hasDescriptions ? item[0] : item;
                const description = hasDescriptions ? item[1] : null;

                return (
                  <tr key={index}>
                    <td>
                      <strong>
                        <code>{value}</code>
                      </strong>
                    </td>
                    {hasDescriptions && (
                      <td>
                        <strong dangerouslySetInnerHTML={{ __html: description }} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {notes && notes.length > 0 && (
        <>
          <br />
          <strong>
            <ul className='style'>
              {notes.map((note, index) => (
                <li key={index}>
                  <code>{note}</code>
                </li>
              ))}
            </ul>
          </strong>
        </>
      )}
    </div>
  );
};

/**
 * API 액션 테이블 생성
 * @param {string} category - 카테고리 이름 (예: '주문', '문의')
 * @param {Array<Object>} actions - 액션 객체 배열
 * @param {string} actions[].action - 액션 이름
 * @param {string} actions[].desc - 액션 설명
 * @param {string} actions[].code - 작업명 코드 (선택)
 * @param {Array<string>} actions[].notes - 주의사항 배열 (선택)
 * @returns {JSX.Element} 액션 테이블 JSX
 */
export const apiActionTable = (category, actions) => {
  return (
    <table width='700px'>
      <tbody>
        <tr>
          <th colSpan='2'>{category}</th>
        </tr>
        {actions.map((actionItem, index) => {
          const { action, desc, code, notes } = actionItem;

          return (
            <tr key={index}>
              <td width='200px'>
                <font color='red'>{action}</font>
              </td>
              <td>
                {desc}
                {code && (
                  <>
                    <br />
                    <code>{code}</code>
                  </>
                )}
                {notes && notes.length > 0 && (
                  <div className='description' style={{ padding: '12px 0' }}>
                    <ul className='style'>
                      {notes.map((note, noteIndex) => (
                        <li key={noteIndex}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
